/**
 * Live CAD map planning for Command Center location (lock 2026-08-19, slice 3).
 *
 * Grouping lives here, in SecOps code, on purpose. A renderer's screen-space cluster would
 * merge two officers standing on different properties just because the pixels overlap, and it
 * would happily invent a shared centre for people who have no site at all. Site and area are
 * product facts, so the product owns them.
 *
 * Paint maths (fade, hide, halo, Unassigned key, trail cap) is NOT reimplemented here. This
 * module consumes `duty-live-presentation`.
 */

import {
  UNASSIGNED_SITE_GROUP,
  capLiveTrails,
  liveSiteGroupKey,
  type LiveTrailCandidate,
} from "./duty-live-presentation";
import type { PointFeature, PointFeatureCollection } from "./map-points";

/** At or above this zoom the operator is on a street and wants people, not counts. */
export const LIVE_CLUSTER_STREET_ZOOM = 15;

/** A set this small reads fine as individual marks at any zoom. */
export const LIVE_CLUSTER_SMALL_SET = 8;

/** Below this many members a group is not worth hiding behind a count. */
export const LIVE_CLUSTER_MIN_GROUP = 3;

export interface LiveDutyMember {
  /** Route assignment id. Stable key for the person mark. */
  id: string;
  lng: number;
  lat: number;
  /** Raw site name from the roster. Missing or blank becomes the Unassigned group. */
  siteName: string | null;
  onCall: boolean;
  /** Focus subject, filter hit, or operator selection. Never buried inside a count. */
  alwaysIndividual: boolean;
}

export interface LiveDutyMembersOptions {
  /** Focus subject. Always an individual mark so a Call fly-to never lands on a count. */
  focusRouteAssignmentId?: string | null;
  /** A filtered map is already a narrowed answer, so it shows people rather than counts. */
  filterActive?: boolean;
}

/**
 * Read cluster members straight off the duty ping features the desk is about to paint. Pings
 * already hidden by the age rule are not in that collection, so they never inflate a count.
 */
export function liveDutyMembersFromFeatures(
  collection: PointFeatureCollection,
  options: LiveDutyMembersOptions = {},
): LiveDutyMember[] {
  const members: LiveDutyMember[] = [];
  for (const feature of collection.features) {
    const id = feature.properties.id ?? "";
    const [lng, lat] = feature.geometry.coordinates;
    if (id.length === 0 || !Number.isFinite(lng) || !Number.isFinite(lat)) {
      continue;
    }
    const siteGroup = feature.properties.siteGroup ?? UNASSIGNED_SITE_GROUP;
    members.push({
      id,
      lng,
      lat,
      siteName: siteGroup === UNASSIGNED_SITE_GROUP ? null : siteGroup,
      onCall: feature.properties.onCall === "true",
      alwaysIndividual:
        options.filterActive === true ||
        (options.focusRouteAssignmentId !== null &&
          options.focusRouteAssignmentId !== undefined &&
          options.focusRouteAssignmentId === id),
    });
  }
  return members;
}

export interface LiveDutyCluster {
  /** Cluster key. Also the entity id suffix the adapter paints. */
  key: string;
  siteGroup: string;
  unassigned: boolean;
  count: number;
  onCallCount: number;
  /**
   * Mean of member coordinates. Camera framing and the count bubble only. Members keep their
   * true coordinates when the cluster expands; nothing is ever snapped to a parcel.
   */
  center: [number, number];
  memberIds: string[];
}

export interface LiveDutyClusterPlan {
  clusters: LiveDutyCluster[];
  /** Members painted as individual person marks at their true coordinates. */
  individualIds: Set<string>;
  clustered: boolean;
}

export interface LiveDutyClusterOptions {
  zoom: number;
  /** Cluster keys the operator has opened by clicking the count. */
  expandedKeys?: ReadonlySet<string>;
}

function meanCenter(members: readonly LiveDutyMember[]): [number, number] {
  let lng = 0;
  let lat = 0;
  for (const member of members) {
    lng += member.lng;
    lat += member.lat;
  }
  const count = members.length === 0 ? 1 : members.length;
  return [lng / count, lat / count];
}

/**
 * Decide which duty people paint as individual marks and which collapse into a site or
 * Unassigned count. Street zoom, a small account, an expanded cluster, and the focused subject
 * all force individual marks.
 */
export function planLiveDutyClusters(
  members: readonly LiveDutyMember[],
  options: LiveDutyClusterOptions,
): LiveDutyClusterPlan {
  const allIndividual = (): LiveDutyClusterPlan => ({
    clusters: [],
    individualIds: new Set(members.map((member) => member.id)),
    clustered: false,
  });

  if (
    members.length <= LIVE_CLUSTER_SMALL_SET ||
    (Number.isFinite(options.zoom) && options.zoom >= LIVE_CLUSTER_STREET_ZOOM)
  ) {
    return allIndividual();
  }

  const expanded = options.expandedKeys ?? new Set<string>();
  const groups = new Map<string, LiveDutyMember[]>();
  const individualIds = new Set<string>();

  for (const member of members) {
    if (member.alwaysIndividual) {
      individualIds.add(member.id);
      continue;
    }
    const key = liveSiteGroupKey(member.siteName);
    const bucket = groups.get(key);
    if (bucket === undefined) {
      groups.set(key, [member]);
    } else {
      bucket.push(member);
    }
  }

  const clusters: LiveDutyCluster[] = [];
  for (const [key, bucket] of groups) {
    if (bucket.length < LIVE_CLUSTER_MIN_GROUP || expanded.has(key)) {
      for (const member of bucket) {
        individualIds.add(member.id);
      }
      continue;
    }
    clusters.push({
      key,
      siteGroup: key,
      unassigned: key === UNASSIGNED_SITE_GROUP,
      count: bucket.length,
      onCallCount: bucket.filter((member) => member.onCall).length,
      center: meanCenter(bucket),
      memberIds: bucket.map((member) => member.id),
    });
  }

  clusters.sort((left, right) => left.key.localeCompare(right.key));
  return { clusters, individualIds, clustered: clusters.length > 0 };
}

/** Count copy for a cluster bubble. Officer, site, Unassigned; never a coordinate. */
export function liveClusterLabel(cluster: LiveDutyCluster): string {
  return `${cluster.count} officer${cluster.count === 1 ? "" : "s"}`;
}

/**
 * Cluster bubbles as GeoJSON so the adapter reads one shape for every point layer.
 * Properties carry counts and group names only; popups never dump coordinates.
 */
export function toLiveClusterFeatureCollection(
  clusters: readonly LiveDutyCluster[],
): PointFeatureCollection {
  return {
    type: "FeatureCollection",
    features: clusters.map((cluster): PointFeature => {
      const properties: Record<string, string> = {
        id: cluster.key,
        name: cluster.siteGroup,
        count: String(cluster.count),
        countLabel: liveClusterLabel(cluster),
        unassigned: cluster.unassigned ? "true" : "false",
        layerLabel: cluster.unassigned
          ? "No site on these pings. Click to open."
          : "Click to open this site group.",
      };
      if (cluster.onCallCount > 0) {
        properties.onCallCount = String(cluster.onCallCount);
        properties.note = `${cluster.onCallCount} on Call`;
      }
      return {
        type: "Feature",
        geometry: { type: "Point", coordinates: cluster.center },
        properties,
      };
    }),
  };
}

export interface LiveTrailPlan {
  drawnIds: string[];
  remainderIds: string[];
  /** Honest copy for the desk when Live wanted more trails than the cap allows. */
  remainderNote: string | null;
}

/**
 * Live trail budget. Idle Live still prefers one focus-driven trail, so today this is called
 * with at most one candidate. It exists as the single seam so multi-trail Live cannot land
 * later without the 12-trail cap and the honest remainder line coming with it.
 */
export function planLiveTrails(candidates: readonly LiveTrailCandidate[]): LiveTrailPlan {
  const capped = capLiveTrails(candidates);
  return {
    ...capped,
    remainderNote:
      capped.remainderIds.length === 0
        ? null
        : `+${capped.remainderIds.length} officer${capped.remainderIds.length === 1 ? "" : "s"} not drawn.`,
  };
}
